import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Comment } from '@mui/icons-material';
import { IFlattenedPhotoItem } from '../../../../models/phases-of-contruction';

interface IPhotoItemProps {
  photo: IFlattenedPhotoItem;
  onPhotoClick: (photo: IFlattenedPhotoItem) => void;
}

export const PhotoItem: React.FC<IPhotoItemProps> = ({ photo, onPhotoClick }) => {
  const [showCaption, setShowCaption] = useState(false);

  const toggleCaption = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowCaption(prev => !prev);
  };

  const handlePhotoClick = () => {
    onPhotoClick(photo);
  };

  return (
    <Box sx={{ px: 1 }}>
      <Box
        onClick={handlePhotoClick}
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 200, sm: 250, md: 300 },
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 2,
          mx: 'auto',
          cursor: 'pointer',
          '&:hover': {
            transform: 'scale(1.02)',
            transition: 'transform 0.2s ease-in-out'
          }
        }}
      >
        <Box
          component="img"
          src={photo.photoUrl}
          alt={photo.originalFileName || 'Build photo'}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
          }}
        />

        {photo.comment && (
          <Tooltip title="Click to view photo comments" arrow>
            <IconButton
              onClick={toggleCaption}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                color: 'primary.dark',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                  transform: 'scale(1.1)'
                }
              }}
            >
              <Comment fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '50%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.7))',
            color: 'white',
            opacity: showCaption ? 1 : 0,
            transform: showCaption ? 'translateY(0)' : 'translateY(10px)',
            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
            pointerEvents: showCaption ? 'auto' : 'none'
          }}
        >
          <Box
            sx={{
              height: { xs: '100px', sm: '125px', md: '150px' },
              overflowY: 'auto',
              p: 2,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '3px',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.7)' }
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: '14px', lineHeight: 1.4, wordBreak: 'break-word' }}
            >
              {photo.comment}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
